import * as React from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';
import AppBar from '@mui/material/AppBar';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Button from '@mui/material/Button';
import ewharter from '../ewharter.jpeg';
import wharter from '../wharter.jpg';

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`full-width-tabpanel-${index}`}
      aria-labelledby={`full-width-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `full-width-tab-${index}`,
    'aria-controls': `full-width-tabpanel-${index}`,
  };
}

export default function FullWidthTabs() {
  const theme = useTheme();
  const [value, setValue] = React.useState(0);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  return (
    <Box sx={{ bgcolor: 'transparent', width: '100vw' }}>
      <AppBar position="static" elevation={0} sx={{ backgroundColor: 'transparent' }}>
        <Tabs
          value={value}
          onChange={handleChange}
          indicatorColor="secondary"
          textColor="inherit"
          variant="fullWidth"
          aria-label="full width tabs example"
          sx={{ color: '#fff' }}
        >
          <Tab label="Home" {...a11yProps(0)} sx={{ color: '#fff' }} />
          <Tab label="About" {...a11yProps(1)} sx={{ color: '#fff' }} />
          <Tab label="Contact" {...a11yProps(2)} sx={{ color: '#fff' }} />
        </Tabs>
      </AppBar>
      <TabPanel value={value} index={0} dir={theme.direction}>
        <span style={{ color: '#fff', textAlign: 'left' }}>
          <Alert variant="filled" severity="primary">
            Synthetic Data Systems is a cutting-edge synthetic data startup specializing in defense and geospatial intelligence (GIS) applications. We deliver highly accurate datasets that closely replicate existing real-world data or create entirely new datasets meticulously tailored to address specific, complex problem sets. Our focus is on providing high-fidelity points, lines, polygons, and raster data optimized for GIS systems, enabling defense and geospatial professionals to run advanced scenarios, derive actionable insights, and enhance AI/ML model training. Our secure, scalable solutions empower clients to innovate and solve mission-critical challenges while maintaining the highest standards of data privacy and operational efficiency.
          </Alert>
        </span>
        <div>
          <h3>Products</h3>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', gap: 3, flexWrap: 'wrap', mt: 2 }}>
          <Card sx={{ maxWidth: 345, m: 1 }}>
            <CardContent>
              <Typography gutterBottom variant="h5" component="div">
                Adaptive Threat Landscape Generator (ATLG)
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                The Adaptive Threat Landscape Generator (ATLG) is an innovative tool from Synthetic Data Systems, designed to revolutionize defense and geospatial intelligence (GIS) applications for DoD and DHS missions. ATLG generates high-fidelity synthetic GIS datasets—points, lines, polygons, and rasters—tailored to simulate dynamic threat scenarios, such as urban insurgencies or border infiltrations. Its unique real-time adaptive feedback loop allows the AI to iteratively refine datasets based on simulation outcomes, ensuring evolving, realistic scenarios for wargaming, mission planning, and AI/ML model training. Accessible via our web app, ATLG delivers secure, scalable, and customizable datasets, empowering analysts to derive actionable insights while maintaining data privacy and operational readiness. 

                <Alert>This is in development and will be available soon in web and as a QGIS plugin.</Alert>
                <br />
                <Button variant='contained' disabled>Log in to try</Button>
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ maxWidth: 345, m: 1 }}>
            <CardContent>
              <Typography gutterBottom variant="h5" component="div">
                Universal Data Forge (UDF)
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                The Universal Data Forge (UDF) is a versatile, industry-agnostic synthetic data platform from Synthetic Data Systems, enabling any organization—be it healthcare, finance, retail, or beyond—to create privacy-compliant, tailored datasets for AI/ML training and analytics. Hosted on our web app, UDF’s no-code “Data Blueprint” wizard lets users generate tabular, time-series, or geospatial data (e.g., CSV, JSON, GeoJSON) via intuitive controls, mimicking real-world patterns or simulating novel scenarios. Its adaptive AI ensures high-fidelity outputs, making UDF a scalable, secure solution for accelerating innovation across industries without technical barriers.
                <Alert>This is in development and will be available soon in web and as a QGIS plugin.</Alert>
                <br />
                <Button variant='contained' disabled>Log in to try</Button>
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ maxWidth: 345, m: 1 }}>
            <CardContent>
              <Typography gutterBottom variant="h5" component="div">
                Resilient Infrastructure Mapper (RIM)
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                This product synthesizes GIS data for critical infrastructure under stress, such as power grids or transportation networks during cyberattacks or natural disasters. Users specify scenarios (e.g., EMP blast radius), and it generates synthetic rasters for damage overlays, polygons for affected areas, lines for evacuation routes, and points for asset vulnerabilities. Novelty: Hybrid real-synthetic fusion, where it blends public FEMA datasets with generated "gap-filling" data for incomplete real-world maps, enabling zero-shot AI training on unseen threats. Ideal for DHS disaster response and DoD base protection. Acquisition draw: Its fusion technique as a patented process could appeal to Esri or IBM for expanding their resilience modeling tools, positioning your company as a niche innovator in infrastructure security.
                <br />

                <Alert>This is in development and will be available soon in web and as a QGIS plugin.</Alert>
                <br />
                <Button variant='contained' disabled>Log in to try</Button>
              </Typography>
            </CardContent>
          </Card>
        </Box>
        </div>
      </TabPanel>
      <TabPanel value={value} index={1} dir={theme.direction}>
        <span style={{ color: '#fff' }}>
          <Alert variant="filled" severity="success">
            We are a father / son team dedicated to advancing synthetic data solutions for defense and geospatial intelligence. Our mission is to provide cutting-edge, high-fidelity synthetic datasets that empower organizations to enhance their analytics, optimize AI systems, and ensure operational readiness in complex environments.
          </Alert>
        </span>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', gap: 3, flexWrap: 'wrap', mt: 2 }}>
          <Card sx={{ maxWidth: 345, m: 1 }}>
            <CardMedia
              sx={{ height: 375 }}
              image={ewharter}
              title="Earl W. Harter Jr."
            />
            <CardContent>
              <Typography gutterBottom variant="h5" component="div">
                Earl W. Harter Jr.
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                CEO Earl W. Harter Jr. has over 50 years of experience in the defense and homeland security sectors. He retired as a Colonel from the U.S. Army in 2001.  He was a political appointee for logistics coordination at the Pentagon.  From 2003 to 2005, he was the Chief of Staff of Operational Integration for the newly formed Department of Homeland Security.
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ maxWidth: 345, m: 1 }}>
            <CardMedia
              sx={{ height: 375 }}
              image={wharter}
              title="William B. Harter"
            />
            <CardContent>
              <Typography gutterBottom variant="h5" component="div">
                William B. Harter
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                CTO William B. Harter has over 27 years of experience working for the DoD as an all source analyst, geospatial engineer and software developer.  He has supported multiple agenices, both as a full time reservist and as a government contractor.  He has extensive experience with geospatial data, GIS systems, data science and AI/ML model development.
              </Typography>
            </CardContent>
          </Card>
        </Box>

      </TabPanel>
      <TabPanel value={value} index={2} dir={theme.direction}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', gap: 3, flexWrap: 'wrap', mt: 2 }}>
          <Card sx={{ maxWidth: 345, m: 1 }}>
            <CardContent>
              <Typography gutterBottom variant="h5" component="div">
                Contact Us
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                You can contact us at <a href="mailto:contact@syndatasys.com">contact@syndatasys.com</a>
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </TabPanel>
    </Box>
  );
}